import React from "react";
import { AlertTriangle, Home, RefreshCcw } from "lucide-react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: any;
}

const ErrorPageFallback: React.FC<{ error: any }> = ({ error }) => {
  console.error("Application Error:", error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 w-full">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-gray-100 p-8 text-center space-y-6">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-red-500" />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-900">
            Oops! Something went wrong
          </h1>
          <p className="text-gray-500 text-sm">
            We encountered an unexpected error. Our team has been notified.
          </p>
        </div>

        {error && import.meta.env.DEV && (
          <div className="bg-gray-50 rounded-lg p-4 text-left border border-gray-200">
            <p className="text-xs font-mono text-red-600 break-all">
              {error.statusText || error.message || "Unknown error"}
            </p>
            {error.stack && (
              <details className="mt-2 text-xs text-gray-400 cursor-pointer">
                <summary>View Stack Trace</summary>
                <div className="mt-2 p-2 bg-gray-100 rounded overflow-auto max-h-32">
                  {error.stack}
                </div>
              </details>
            )}
          </div>
        )}

        <div className="flex gap-3 justify-center pt-2">
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-md bg-white hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            <RefreshCcw className="w-4 h-4" />
            Reload Page
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white transition-colors text-sm font-medium"
          >
            <Home className="w-4 h-4" />
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, info: any) {
    console.error("Runtime error caught:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorPageFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}
